import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Session state management
let sessionExpiredModalCallback = null;
let isRefreshing = false;
let failedQueue = [];

// Process queued requests after token refresh
const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

// Axios instance — withCredentials sends the httpOnly `access_token` cookie automatically.
// The Authorization header path remains as backward-compatible fallback for legacy code.
const api = axios.create({
  baseURL: `${API_URL}/api`,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add token to requests if available
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('portal_token') || localStorage.getItem('worker_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle 401 errors (token expired) with smart handling
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    
    // Only handle 401 errors
    if (error.response?.status !== 401) {
      return Promise.reject(error);
    }
    
    const detail = error.response?.data?.detail;
    
    // If token is completely invalid (not just expired), clear and redirect
    if (detail === 'INVALID_TOKEN' || detail === 'TOKEN_MISSING' || detail === 'Authorization header missing') {
      clearSessionAndRedirect();
      return Promise.reject(error);
    }
    
    // For expired tokens, try to refresh first
    if (detail === 'TOKEN_EXPIRED' || detail === 'Token has expired') {
      // Don't retry refresh requests
      if (originalRequest.url.includes('/auth/refresh')) {
        showSessionExpiredModal();
        return Promise.reject(error);
      }
      
      // If already refreshing, queue this request
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then(token => {
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return api(originalRequest);
        }).catch(err => {
          return Promise.reject(err);
        });
      }
      
      isRefreshing = true;
      
      try {
        // Try to refresh the token
        const token = localStorage.getItem('portal_token');
        const response = await axios.post(
          `${API_URL}/api/auth/refresh`,
          {},
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        if (response.data.success && response.data.token) {
          const newToken = response.data.token;
          // Backend already refreshed the httpOnly access_token cookie; we don't
          // store the JWT in localStorage anymore (XSS-safe).

          // Update default header so legacy code paths that read it still work
          api.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
          
          // Process queued requests
          processQueue(null, newToken);
          
          // Retry original request
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          return api(originalRequest);
        }
      } catch (refreshError) {
        processQueue(refreshError, null);
        showSessionExpiredModal();
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }
    
    // Default: clear session and redirect
    clearSessionAndRedirect();
    return Promise.reject(error);
  }
);

// Clear session and redirect to login
const clearSessionAndRedirect = () => {
  localStorage.removeItem('portal_token');
  localStorage.removeItem('portal_user');
  localStorage.removeItem('worker_token');
  window.location.href = '/login';
};

// Show session expired modal with magic link option
const showSessionExpiredModal = () => {
  if (sessionExpiredModalCallback) {
    try {
      const userData = JSON.parse(localStorage.getItem('portal_user') || '{}');
      sessionExpiredModalCallback(userData.email);
    } catch (e) {
      sessionExpiredModalCallback(null);
    }
  } else {
    // Fallback: redirect to login
    clearSessionAndRedirect();
  }
};

// Set callback for session expired modal
export const setSessionExpiredCallback = (callback) => {
  sessionExpiredModalCallback = callback;
};

// Auth API - uses unified auth system
export const authAPI = {
  getCurrentUser: () => api.get('/auth/me'),
  verifyToken: () => api.get('/auth/verify'),
  refreshToken: () => api.post('/auth/refresh'),
  requestMagicLink: (email) => api.post('/auth/request-magic-link', null, { params: { email } }),
  loginPin: (email, pin) => api.post('/auth/login-pin', { email, pin }),
  pinStatus: () => api.get('/auth/pin-status'),
  createPin: (pin, pin_confirm) => api.post('/auth/create-pin', { pin, pin_confirm }),
  changePin: (current_pin, new_pin, new_pin_confirm) => api.put('/auth/change-pin', { current_pin, new_pin, new_pin_confirm }),
  resetPin: (user_email) => api.post('/auth/reset-pin', { user_email }),
  logout: () => api.post('/auth/logout'),
};

// Drive API
export const driveAPI = {
  listFolders: (folderId) => api.get('/drive/folders', { params: folderId ? { folder_id: folderId } : {} }),
  listFiles: (folderId) => api.get('/drive/files', { params: folderId ? { folder_id: folderId } : {} }),
  getDownloadUrl: (fileId) => api.get(`/drive/download/${fileId}`),
};

export default api;
